import { CareerLevel } from '../types/career';

/**
 * First-year commission payout coefficient for Ukrainian FOP entrepreneurial contracts.
 * Starlife pays 80% in the first year of contract life.
 */
export const FIRST_YEAR_COMMISSION_COEFFICIENT = 0.80;

/**
 * Base career level rates in UAH per Accounting Unit (АЕ - Акумуляційна Одиниця).
 * Starlife official career rate scale:
 * Level 1 (Консультант): 87.30 UAH/AE
 * Level 2 (Субагент): 116.40 UAH/AE
 * Level 3 (Менеджер): 169.75 UAH/AE
 * Level 4 (Старший менеджер): 218.00 UAH/AE
 * Level 5 (Менеджер по управлінню): 257.00 UAH/AE
 * Level 6 (Директор): 281.30 UAH/AE
 * Level 7 (Золотий директор): 303.61 UAH/AE
 * Level 8 (Президент / Співвласник): 323.01 UAH/AE
 */
export const CAREER_BASE_RATES: Record<CareerLevel, number> = {
  1: 87.30,
  2: 116.40,
  3: 169.75,
  4: 218.00,
  5: 257.00,
  6: 281.30,
  7: 303.61,
  8: 323.01,
};

/**
 * Director bonus rate per AE for levels 6, 7, 8.
 * Applies to personal AE + turnover of direct structures <= Level 5.
 */
export const DIRECTOR_BONUS_RATE_UAH = 50.00;

/**
 * Co-owner bonus rate per AE for level 8 (President).
 * Combined with Director bonus, gives 150.00 UAH/AE on qualified turnover.
 */
export const CO_OWNER_BONUS_RATE_UAH = 100.00;

/**
 * Tax parameters for Ukrainian FOP 3rd group:
 * - Single Tax: 5% of gross revenue
 * - Military Levy: 1% of gross revenue (effective from recent fiscal regulations)
 * - Monthly Single Social Contribution (ЄСВ / USV): 2,100.12 UAH (22% of projected 2027 minimum wage 9,546 UAH)
 */
export const FOP_SINGLE_TAX_RATE = 0.05;
export const FOP_MILITARY_LEVY_RATE = 0.01;
export const FOP_MONTHLY_USV_UAH = 2100.12;

/**
 * Default standard contract size in AE.
 * A typical 25,000 - 30,000 UAH annual policy equals 50 AE.
 */
export const STANDARD_CONTRACT_AE = 50.0;

/**
 * Get base rate in UAH/AE for a specific career level.
 */
export function getBaseRate(level: CareerLevel): number {
  const rate = CAREER_BASE_RATES[level];
  if (rate === undefined) {
    throw new Error(`Invalid career level: ${level}`);
  }
  return rate;
}

/**
 * Get net rate in UAH/AE (applying the 80% first-year factor).
 */
export function getNetRate(level: CareerLevel): number {
  return getBaseRate(level) * FIRST_YEAR_COMMISSION_COEFFICIENT;
}

/**
 * Calculate base rate difference (UAH/AE) between mentor level and direct branch leader level.
 * Returns 0 if branch leader level is equal to or greater than mentor level.
 */
export function getRateDifference(mentorLevel: CareerLevel, branchLeaderLevel: CareerLevel): number {
  if (mentorLevel <= branchLeaderLevel) {
    return 0;
  }
  const mentorRate = getBaseRate(mentorLevel);
  const branchRate = getBaseRate(branchLeaderLevel);
  return Math.max(0, mentorRate - branchRate);
}

/**
 * Calculate net rate difference (UAH/AE) applying the 80% factor.
 */
export function getNetRateDifference(mentorLevel: CareerLevel, branchLeaderLevel: CareerLevel): number {
  return getRateDifference(mentorLevel, branchLeaderLevel) * FIRST_YEAR_COMMISSION_COEFFICIENT;
}

/**
 * Calculate payout in UAH for a single policy (default 50 AE) for a consultant at a given level.
 */
export function calculateSinglePolicyPayout(level: CareerLevel, contractAE: number = STANDARD_CONTRACT_AE): number {
  return contractAE * getNetRate(level);
}
